import React, { useCallback, useMemo, useRef, useState } from "react";
import Plot from "./PlotlyChart";
import {
  contrastingStrokeForFill,
  getColorForDate,
  getPlotlyLegendStyle,
  sortSpeciesForWeightLengthLegend,
} from "../../graphView/colors";
import { buildLengthTicks, buildWeightTicks } from "./axisTicks";
import { plotlyAxisBase, plotlyAxisTitle } from "./plotlyAxisTheme";
import { plotlyHorizontalLegendForTraces } from "./plotlyLegendLayout";
import {
  GraphV2FullscreenInlineRow,
  useGraphV2PlotHeight,
} from "../ChartFullscreenShell";
import { useSamplingPlotExportRegister } from "../../graphView/useSamplingPlotExportRegister";
import { LABEL } from "../../../../utils/samplingDisplayLabels";
import "./wrVsLengthPlotly.css";

const CARD_W = 300;
const CARD_H = 360;

function formatSignedFixed(n, decimals) {
  const s = n.toFixed(decimals);
  if (n > 0) return `+${s}`;
  return s;
}

export default function WeightVsLengthByDateChart({
  graphData,
  selectedUnit,
  selectedFish,
  isMobile,
}) {
  const wrapRef = useRef(null);
  const plotRef = useRef(null);
  useSamplingPlotExportRegister(plotRef);
  const [tip, setTip] = useState(null);

  const { speciesList, dateKeys } = useMemo(() => {
    const raw = [...new Set(graphData.map((d) => d.species))];
    const s =
      selectedFish?.length > 0
        ? selectedFish.map((f) => f.label)
        : sortSpeciesForWeightLengthLegend(raw);
    const dk = [...new Set(graphData.map((d) => d.sampleDateKey || "unknown"))].sort(
      (a, b) => a.localeCompare(b)
    );
    return { speciesList: s, dateKeys: dk };
  }, [graphData, selectedFish]);

  const lengthUnit = selectedUnit.value === "English" ? "Inch" : "mm";
  const weightUnit = selectedUnit.value === "English" ? "Pound" : "gram";
  const lengthTitle = `Length (${selectedUnit.value === "English" ? "Inch" : "mm"})`;
  const weightTitle = `Weight (${selectedUnit.value === "English" ? "Pound" : "gram"})`;

  const traces = useMemo(() => {
    const t = [];
    /** Species sampled / standard / stocked — appended after date + shape keys so legend reads dates → shapes → data series. */
    const speciesTracesAtLegendEnd = [];

    speciesList.forEach((species) => {
      const rows = graphData
        .filter((d) => d.species === species)
        .sort((a, b) => Number(a.length || 0) - Number(b.length || 0));
      const samp = rows.filter((d) => !d.isStocking);
      const stock = rows.filter((d) => d.isStocking);
      const { symbol: speciesShape } = getPlotlyLegendStyle(species, speciesList);

      if (samp.length) {
        const len = samp.map((r) => r.length);
        const act = samp.map((r) => r.actualWeight);
        const std = samp.map((r) => r.standardWeight);
        const sampFills = samp.map((r) =>
          getColorForDate(r.sampleDateKey || "unknown", dateKeys)
        );

        speciesTracesAtLegendEnd.push({
          type: "scatter",
          mode: "markers",
          name: `${species} (${samp.length} total Sampled)`,
          x: len,
          y: act,
          marker: {
            size: 8,
            opacity: 0.9,
            symbol: speciesShape,
            color: sampFills,
            line: {
              width: 2,
              color: sampFills.map((c) => contrastingStrokeForFill(c)),
            },
          },
          customdata: samp.map((r) => {
            const dk = r.sampleDateKey || "unknown";
            return [
              species,
              r.sampleDateLabel || dk,
              Number(r.standardWeight || 0),
              getColorForDate(dk, dateKeys),
              false,
            ];
          }),
          hovertemplate: "<extra></extra>",
        });

        speciesTracesAtLegendEnd.push({
          type: "scatter",
          mode: "lines",
          name: `${species} ${LABEL.STANDARD_WS_TRACE}`,
          x: len,
          y: std,
          line: { color: "#6b7280", width: 2, dash: "dash" },
          hoverinfo: "skip",
        });
      }

      if (stock.length) {
        const len = stock.map((r) => r.length);
        const act = stock.map((r) => r.actualWeight);
        const stockFills = stock.map((r) =>
          getColorForDate(r.sampleDateKey || "unknown", dateKeys)
        );
        speciesTracesAtLegendEnd.push({
          type: "scatter",
          mode: "markers",
          name: `${species} (stocked) (${stock.length})`,
          x: len,
          y: act,
          marker: {
            size: 9,
            opacity: 0.95,
            symbol: speciesShape,
            color: stockFills,
            line: {
              width: 2,
              color: stockFills.map((c) => contrastingStrokeForFill(c)),
            },
          },
          customdata: stock.map((r) => {
            const dk = r.sampleDateKey || "unknown";
            return [
              species,
              r.sampleDateLabel || dk,
              Number(r.standardWeight || 0),
              getColorForDate(dk, dateKeys),
              true,
            ];
          }),
          hovertemplate: "<extra></extra>",
        });
      }
    });

    dateKeys.forEach((dk) => {
      const label =
        graphData.find((d) => (d.sampleDateKey || "unknown") === dk)?.sampleDateLabel || dk;
      const dkFill = getColorForDate(dk, dateKeys);
      t.push({
        type: "scatter",
        mode: "markers",
        name: `Date: ${label}`,
        x: [null],
        y: [null],
        marker: {
          size: 10,
          color: dkFill,
          symbol: "square",
          line: { width: 2, color: contrastingStrokeForFill(dkFill) },
        },
        hoverinfo: "skip",
      });
    });

    speciesList.forEach((species) => {
      const { symbol: spSym } = getPlotlyLegendStyle(species, speciesList);
      t.push({
        type: "scatter",
        mode: "markers",
        name: `Shape: ${species}`,
        x: [null],
        y: [null],
        marker: { size: 10, color: "#4b5563", symbol: spSym },
        hoverinfo: "skip",
      });
    });

    return t.concat(speciesTracesAtLegendEnd);
  }, [graphData, speciesList, dateKeys]);

  const legendLayout = useMemo(
    () => plotlyHorizontalLegendForTraces(traces, isMobile),
    [traces, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: 520,
    desktopDefault: 720,
    legendExtra: legendLayout.extraHeight || 0,
  });

  const xTicks = useMemo(
    () =>
      buildLengthTicks(
        graphData.map((d) => Number(d.length || 0)),
        selectedUnit.value
      ),
    [graphData, selectedUnit.value]
  );
  const yTicks = useMemo(
    () =>
      buildWeightTicks(
        graphData.map((d) =>
          Number(Math.max(d.actualWeight || 0, d.standardWeight || 0))
        ),
        selectedUnit.value,
        20
      ),
    [graphData, selectedUnit.value]
  );

  const xAxisLayout = useMemo(
    () => ({
      title: plotlyAxisTitle(lengthTitle, isMobile),
      tickmode: "array",
      tickvals: xTicks,
      range: [xTicks[0] || 0, xTicks[xTicks.length - 1] || 0],
      tickangle: isMobile ? -45 : 0,
      ...plotlyAxisBase(isMobile),
    }),
    [lengthTitle, isMobile, xTicks]
  );

  const yAxisLayout = useMemo(
    () => ({
      title: plotlyAxisTitle(weightTitle, isMobile),
      rangemode: "tozero",
      tickmode: "array",
      tickvals: yTicks,
      range: [0, yTicks[yTicks.length - 1] || 0],
      ...plotlyAxisBase(isMobile),
    }),
    [weightTitle, isMobile, yTicks]
  );

  const onHover = useCallback(
    (eventData) => {
      const pt = eventData?.points?.[0];
      const el = wrapRef.current;
      const evt = eventData?.event;
      if (
        !pt ||
        el == null ||
        !Number.isFinite(pt.x) ||
        !Number.isFinite(pt.y)
      ) {
        return;
      }
      const traceMode = pt.data?.mode || pt.fullData?.mode;
      if (traceMode !== "markers") {
        return;
      }
      const cd = pt.customdata;
      if (!Array.isArray(cd) || cd.length < 4) {
        return;
      }
      const isStocking = cd[4] === true;

      let cx;
      let cy;
      if (evt && typeof evt.clientX === "number" && typeof evt.clientY === "number") {
        cx = evt.clientX;
        cy = evt.clientY;
      } else if (pt.bbox) {
        cx = (pt.bbox.x0 + pt.bbox.x1) / 2;
        cy = pt.bbox.y0;
      } else {
        return;
      }

      const species = String(cd[0] ?? "");
      const dateLabel = String(cd[1] ?? "");
      const standardWeight = Number(cd[2]);
      const pointColor = String(cd[3] ?? "#059669");
      const length = pt.x;
      const actualWeight = pt.y;
      const diff = actualWeight - standardWeight;
      const pct = standardWeight > 0 ? (actualWeight / standardWeight - 1) * 100 : 0;
      const aboveStandard = actualWeight >= standardWeight;

      const diffClass =
        diff > 1e-6
          ? "wr-length-hover-card__value--diff-pos"
          : diff < -1e-6
            ? "wr-length-hover-card__value--diff-neg"
            : "wr-length-hover-card__value--diff-zero";

      const rect = el.getBoundingClientRect();
      const pad = 12;
      let left = cx - rect.left + pad;
      let top = cy - rect.top + pad;
      if (left + CARD_W > rect.width - 8) {
        left = cx - rect.left - CARD_W - pad;
      }
      if (top + CARD_H > rect.height - 8) {
        top = cy - rect.top - CARD_H - pad;
      }
      left = Math.max(8, Math.min(left, rect.width - CARD_W - 8));
      top = Math.max(8, Math.min(top, rect.height - CARD_H - 8));

      setTip({
        left,
        top,
        species,
        dateLabel,
        pointColor,
        length,
        actualWeight,
        standardWeight,
        diff,
        pct,
        diffClass,
        aboveStandard,
        isStocking,
      });
    },
    []
  );

  const onUnhover = useCallback(() => setTip(null), []);

  return (
    <div
      ref={wrapRef}
      className="weight-vs-length-by-date-plotly-wrap"
      style={{ position: "relative", width: "100%" }}
    >
      <GraphV2FullscreenInlineRow />
      <Plot
        ref={plotRef}
        data={traces}
        layout={{
          autosize: true,
          height: plotHeight,
          margin: { t: legendLayout.marginTop, r: 40, b: isMobile ? 110 : 90, l: 80 },
          paper_bgcolor: "white",
          plot_bgcolor: "white",
          xaxis: xAxisLayout,
          yaxis: yAxisLayout,
          legend: legendLayout.legend,
          hovermode: "closest",
          hoverdistance: 8,
        }}
        style={{ width: "100%" }}
        useResizeHandler
        onHover={onHover}
        onUnhover={onUnhover}
      />
      {tip && (
        <div
          className={`wr-length-hover-card wr-length-hover-card--by-date${
            isMobile ? " wr-length-hover-card--mobile" : ""
          }`}
          style={{ left: tip.left, top: tip.top }}
          role="tooltip"
        >
          <div className="wr-length-hover-card__body">
            <p className="wr-length-hover-card__title">{tip.species}</p>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">
                {tip.isStocking ? "Stocked date:" : "Sampling date:"}
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--date">
                {tip.dateLabel}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Length:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--length">
                {tip.length.toFixed(2)} {lengthUnit}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label wr-length-hover-card__label-with-swatch">
                <span
                  className="wr-length-hover-card__dot-swatch"
                  style={{ backgroundColor: tip.pointColor }}
                  aria-hidden
                />
                Actual Weight:
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--actual">
                {tip.actualWeight.toFixed(2)} {weightUnit}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label wr-length-hover-card__label-with-swatch">
                <span className="wr-length-hover-card__dash-swatch" aria-hidden />
                Standard Weight:
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--standard">
                {Number.isFinite(tip.standardWeight)
                  ? `${tip.standardWeight.toFixed(2)} ${weightUnit}`
                  : "—"}
              </span>
            </div>
            <div className="wr-length-hover-card__subgrid">
              <div className="wr-length-hover-card__row">
                <span className="wr-length-hover-card__label">Difference:</span>
                <span className={`wr-length-hover-card__value ${tip.diffClass}`}>
                  {Number.isFinite(tip.diff)
                    ? `${formatSignedFixed(tip.diff, 2)} ${weightUnit}`
                    : "—"}
                </span>
              </div>
              <div className="wr-length-hover-card__row">
                <span className="wr-length-hover-card__label">Percentage:</span>
                <span className={`wr-length-hover-card__value ${tip.diffClass}`}>
                  {Number.isFinite(tip.pct) ? `${formatSignedFixed(tip.pct, 1)}%` : "—"}
                </span>
              </div>
            </div>
          </div>
          <div
            className={
              tip.aboveStandard
                ? "wr-length-hover-card__badge wr-length-hover-card__badge--ok"
                : "wr-length-hover-card__badge wr-length-hover-card__badge--low"
            }
          >
            {tip.aboveStandard
              ? "✓ At or above standard weight for this length"
              : "Below standard weight for this length"}
          </div>
        </div>
      )}
    </div>
  );
}
